// MBA Lab — Leadership Scenario Simulator UI. Imports only lib/core.js and
// lib/simulator-model.js. Event delegation over data-simulator-scenario means
// the six flat-scenario radiogroups never need individually-wired listeners;
// the six deep-tree bodies are fully JS-rendered/re-rendered on every state
// change instead, so their buttons get direct listeners at creation time.
import { store, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  SCHEMA_VERSION,
  TOTAL_SCENARIOS,
  TOTAL_TREES,
  LIMITS,
  createInitialState,
  parseSavedState,
  serializeForSave,
  selectChoice,
  countExplored,
  getDebrief,
  getTreeProgress,
  countTreesCompleted,
  getCurrentTreeNode,
  getTreeDebrief,
  checkTreeWriteDraft,
  startTree,
  restartTree,
  chooseTreeOption,
  submitTreeWrite,
} from './lib/simulator-model.js';
import { SCENARIOS } from './lib/simulator-scenarios.js';
import { TREES } from './lib/simulator-trees.js';

const TOOL_NAME = 'leadership-scenario-simulator';
const AUTOSAVE_DEBOUNCE_MS = 400;
const ANNOUNCE_DEBOUNCE_MS = 1200;

const OUTCOME_LABELS = Object.freeze({
  resolved: 'Resolved',
  partial: 'Partially resolved',
  stalled: 'Stalled',
  damaged: 'Damaged',
});

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class LeadershipScenarioSimulator {
  constructor() {
    this.container = byId('simulator-scenarios');
    this.progressText = byId('simulator-progress-text');
    this.debriefAnnouncer = byId('simulator-debrief-announcer');
    this.treesContainer = byId('simulator-trees');
    this.treeProgressText = byId('simulator-tree-progress-text');
    this.startOverBtn = byId('simulator-start-over');
    this.actionFeedback = byId('simulator-action-feedback');
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.simulatorStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.saveTimer = null;
    this.announceTimer = null;
    this.startOverConfirming = false;
    this.startOverTimer = null;
    this.hasEngaged = false;
    this.firedResultFor = new Set();
    this.firedTreeResultFor = new Set();

    this.state = this.loadInitialState();
    this.applyStateToForm();
    this.renderAllDebriefs();
    this.renderProgress();
    this.wireEvents();
    this.renderAllTreeBodies();
    this.renderTreeProgress();
    trackToolAction(TOOL_NAME, 'open');
  }

  loadInitialState() {
    const saved = this.simulatorStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  /** Fully authoritative, not just additive: also unchecks any input that
   * doesn't match current state, so calling this after a reset (state back
   * to no selections) actually clears the visible radio buttons too, not
   * just the underlying data. */
  applyStateToForm() {
    for (const scenario of SCENARIOS) {
      const choiceId = this.state.selections[scenario.id];
      const inputs = this.container.querySelectorAll(`input[data-simulator-scenario="${scenario.id}"]`);
      for (const input of inputs) input.checked = choiceId ? input.value === choiceId : false;
    }
  }

  wireEvents() {
    this.container.addEventListener('change', (e) => this.handleChoiceChange(e));
    this.startOverBtn.addEventListener('click', () => this.handleStartOver());
  }

  handleChoiceChange(e) {
    const el = e.target.closest('input[data-simulator-scenario]');
    if (!el || !this.container.contains(el)) return;
    const scenarioId = el.dataset.simulatorScenario;
    const choiceId = el.value;
    this.state = selectChoice(this.state, scenarioId, choiceId);
    this.markEngaged();
    const debriefText = this.renderDebrief(scenarioId);
    this.renderProgress();
    this.scheduleAutosave();
    this.maybeTrackResult(scenarioId);
    if (debriefText) this.scheduleAnnouncement(debriefText);
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  maybeTrackResult(scenarioId) {
    if (this.firedResultFor.has(scenarioId)) return;
    this.firedResultFor.add(scenarioId);
    // Coarse params only - which scenario and which choice slot (a/b/c), never
    // free text, matching every other tool's analytics discipline.
    const choiceId = this.state.selections[scenarioId];
    trackToolAction(TOOL_NAME, 'result', { scenario_id: scenarioId, choice_id: choiceId });
  }

  renderAllDebriefs() {
    for (const scenario of SCENARIOS) this.renderDebrief(scenario.id);
  }

  /** @returns {string|null} the rendered debrief text, for the caller to announce - null when no choice is selected yet. */
  renderDebrief(scenarioId) {
    const debriefEl = this.container.querySelector(`[data-simulator-debrief="${scenarioId}"]`);
    if (!debriefEl) return null;

    const result = getDebrief(this.state, scenarioId);
    if (!result) {
      debriefEl.hidden = true;
      debriefEl.textContent = '';
      return null;
    }

    const scenario = SCENARIOS.find((s) => s.id === scenarioId);
    debriefEl.hidden = false;
    debriefEl.textContent = '';

    const debriefP = document.createElement('p');
    debriefP.className = 'tool-simulator-debrief-text';
    debriefP.textContent = result.choice.debrief;
    debriefEl.appendChild(debriefP);

    const directiveP = document.createElement('p');
    directiveP.className = 'tool-simulator-directive-note';
    const label = document.createElement('strong');
    label.textContent = 'When directive leadership fits better: ';
    directiveP.appendChild(label);
    directiveP.appendChild(document.createTextNode(scenario.whenDirective));
    debriefEl.appendChild(directiveP);

    return `${result.choice.debrief} When directive leadership fits better: ${scenario.whenDirective}`;
  }

  renderProgress() {
    const count = countExplored(this.state);
    this.progressText.textContent = `${count} of ${TOTAL_SCENARIOS} scenarios explored`;
  }

  /**
   * Mirrors roi.js's scheduleAnnouncement: the visible debrief text updates
   * immediately on choice, but a screen reader announcing every reconsidered
   * click in quick succession would be noisy. This mirrors the debrief into a
   * separate visually-hidden aria-live region, debounced.
   */
  scheduleAnnouncement(text) {
    if (this.announceTimer) clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      if (this.debriefAnnouncer.textContent !== text) {
        this.debriefAnnouncer.textContent = text;
      }
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  // --- Deep (tree) scenarios ---------------------------------------------

  renderAllTreeBodies() {
    for (const tree of TREES) this.renderTreeBody(tree.id);
  }

  renderTreeProgress() {
    const count = countTreesCompleted(this.state);
    this.treeProgressText.textContent = `${count} of ${TOTAL_TREES} deep scenarios completed`;
  }

  /** Full re-render of one tree's body from current state. `precedingResultText`
   * is the just-clicked choice's immediate consequence line (simulator-trees.js's
   * `result` field) - shown once, at the top of whatever renders next, never
   * persisted to state. */
  renderTreeBody(treeId, precedingResultText = null) {
    const bodyEl = this.treesContainer.querySelector(`[data-simulator-tree-body="${treeId}"]`);
    if (!bodyEl) return;
    clearChildren(bodyEl);

    const tree = TREES.find((t) => t.id === treeId);
    const progress = getTreeProgress(this.state, treeId);

    if (precedingResultText) {
      const resultP = document.createElement('p');
      resultP.className = 'tool-simulator-tree-result';
      resultP.textContent = precedingResultText;
      bodyEl.appendChild(resultP);
    }

    if (!progress) {
      this.renderTreeNotStarted(bodyEl, tree);
      return null;
    }
    if (progress.completedOutcome) {
      return this.renderTreeDebrief(bodyEl, tree, progress);
    }
    const node = getCurrentTreeNode(treeId, progress);
    if (node.kind === 'choice') this.renderTreeChoiceNode(bodyEl, tree, progress, node);
    else this.renderTreeWriteNode(bodyEl, tree, progress, node);
    return null;
  }

  renderTreeNotStarted(bodyEl, tree) {
    const roleP = document.createElement('p');
    roleP.className = 'tool-simulator-tree-role';
    roleP.textContent = tree.role;
    bodyEl.appendChild(roleP);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-primary btn-sm';
    btn.textContent = 'Start this scenario';
    btn.addEventListener('click', () => this.handleStartTree(tree.id));
    bodyEl.appendChild(btn);
  }

  renderTreePhaseAndStem(bodyEl, node) {
    if (node.phase) {
      const phaseP = document.createElement('p');
      phaseP.className = 'tool-simulator-tree-phase';
      phaseP.textContent = node.phase;
      bodyEl.appendChild(phaseP);
    }
    const stemP = document.createElement('p');
    stemP.className = 'tool-simulator-tree-stem';
    stemP.textContent = node.stem;
    bodyEl.appendChild(stemP);
    if (node.state) {
      const stateP = document.createElement('p');
      stateP.className = 'tool-simulator-tree-state';
      stateP.textContent = node.state;
      bodyEl.appendChild(stateP);
    }
  }

  renderTreeChoiceNode(bodyEl, tree, progress, node) {
    this.renderTreePhaseAndStem(bodyEl, node);

    const optionsDiv = document.createElement('div');
    optionsDiv.className = 'tool-simulator-tree-options';
    for (const option of node.options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tool-form-chip tool-simulator-tree-option';
      btn.textContent = option.text;
      btn.addEventListener('click', () => this.handleChooseTreeOption(tree.id, progress.currentNodeId, option.id));
      optionsDiv.appendChild(btn);
    }
    bodyEl.appendChild(optionsDiv);
  }

  renderTreeWriteNode(bodyEl, tree, progress, node) {
    this.renderTreePhaseAndStem(bodyEl, node);

    const textarea = document.createElement('textarea');
    textarea.className = 'form-control tool-simulator-tree-write-input';
    textarea.rows = 3;
    textarea.maxLength = LIMITS.writeResponseMaxLength;
    textarea.setAttribute('aria-label', node.stem);
    bodyEl.appendChild(textarea);

    const checksList = document.createElement('ul');
    checksList.className = 'tool-simulator-tree-checks';
    bodyEl.appendChild(checksList);

    const errorP = document.createElement('p');
    errorP.className = 'tool-simulator-tree-write-error';
    errorP.textContent = 'Write something before continuing.';
    errorP.hidden = true;
    bodyEl.appendChild(errorP);

    const renderChecks = () => {
      clearChildren(checksList);
      for (const check of checkTreeWriteDraft(tree.id, progress.currentNodeId, textarea.value)) {
        const li = document.createElement('li');
        li.className = check.passed ? 'tool-simulator-tree-check is-pass' : 'tool-simulator-tree-check';
        li.textContent = check.label;
        checksList.appendChild(li);
      }
    };
    renderChecks();
    textarea.addEventListener('input', renderChecks);

    const submitBtn = document.createElement('button');
    submitBtn.type = 'button';
    submitBtn.className = 'btn btn-primary btn-sm tool-simulator-tree-submit';
    submitBtn.textContent = 'Continue';
    submitBtn.addEventListener('click', () =>
      this.handleSubmitTreeWrite(tree.id, progress.currentNodeId, textarea.value, errorP)
    );
    bodyEl.appendChild(submitBtn);
  }

  renderTreeDebrief(bodyEl, tree, progress) {
    const debrief = getTreeDebrief(tree.id, progress);

    const outcomeP = document.createElement('p');
    outcomeP.className = `tool-simulator-tree-outcome is-${debrief.outcome}`;
    const label = document.createElement('strong');
    label.textContent = `${OUTCOME_LABELS[debrief.outcome]}: `;
    outcomeP.appendChild(label);
    outcomeP.appendChild(document.createTextNode(debrief.summary));
    bodyEl.appendChild(outcomeP);

    const restartBtn = document.createElement('button');
    restartBtn.type = 'button';
    restartBtn.className = 'btn btn-outline-secondary btn-sm';
    restartBtn.textContent = 'Try a different path';
    restartBtn.addEventListener('click', () => this.handleRestartTree(tree.id));
    bodyEl.appendChild(restartBtn);

    return `${OUTCOME_LABELS[debrief.outcome]}: ${debrief.summary}`;
  }

  handleStartTree(treeId) {
    this.state = startTree(this.state, treeId, Date.now());
    this.markEngaged();
    this.renderTreeBody(treeId);
    this.renderTreeProgress();
    this.scheduleAutosave();
  }

  handleChooseTreeOption(treeId, nodeId, optionId) {
    const tree = TREES.find((t) => t.id === treeId);
    const option = tree.nodes[nodeId].options.find((o) => o.id === optionId);
    this.state = chooseTreeOption(this.state, treeId, nodeId, optionId, Date.now());
    this.markEngaged();
    const debriefText = this.renderTreeBody(treeId, option?.result ?? null);
    this.renderTreeProgress();
    this.scheduleAutosave();
    this.maybeTrackTreeResult(treeId);
    if (debriefText) this.scheduleAnnouncement(debriefText);
  }

  handleSubmitTreeWrite(treeId, nodeId, text, errorEl) {
    if (!text || !text.trim()) {
      errorEl.hidden = false;
      return;
    }
    this.state = submitTreeWrite(this.state, treeId, nodeId, text, Date.now());
    this.markEngaged();
    const debriefText = this.renderTreeBody(treeId);
    this.renderTreeProgress();
    this.scheduleAutosave();
    this.maybeTrackTreeResult(treeId);
    if (debriefText) this.scheduleAnnouncement(debriefText);
  }

  handleRestartTree(treeId) {
    this.state = restartTree(this.state, treeId, Date.now());
    this.renderTreeBody(treeId);
    this.renderTreeProgress();
    this.scheduleAutosave();
  }

  maybeTrackTreeResult(treeId) {
    const progress = getTreeProgress(this.state, treeId);
    if (!progress?.completedOutcome || this.firedTreeResultFor.has(treeId)) return;
    this.firedTreeResultFor.add(treeId);
    // Coarse params only - which tree and which outcome, never the written
    // text, matching the flat scenarios' analytics discipline above.
    trackToolAction(TOOL_NAME, 'result', { tree_id: treeId, outcome: progress.completedOutcome });
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.simulatorStore.save(toSave);
  }

  /** Click-again-to-confirm, matching leadership-practice-journal.js's
   * handleStartOver exactly - no browser confirm() dialog, just a
   * text-changing confirm-on-second-click affordance for a destructive
   * action. Needed now that the deep trees can hold real written text, not
   * just which choice was picked. */
  handleStartOver() {
    if (!this.startOverConfirming) {
      this.startOverConfirming = true;
      this.startOverBtn.textContent = 'Click again to confirm';
      this.startOverTimer = setTimeout(() => {
        this.startOverConfirming = false;
        this.startOverBtn.textContent = 'Delete all my data';
      }, 4000);
      return;
    }

    clearTimeout(this.startOverTimer);
    this.startOverConfirming = false;
    this.startOverBtn.textContent = 'Delete all my data';
    this.simulatorStore.deleteAccountState();
    this.state = createInitialState();
    // Also reset the engagement/result analytics gates - without this, redoing
    // a scenario in the same page session after a full data delete silently
    // skips the 'engage'/'result' events for what is, from the tool's own
    // now-empty state, a genuinely fresh run.
    this.hasEngaged = false;
    this.firedResultFor = new Set();
    this.firedTreeResultFor = new Set();
    this.applyStateToForm();
    this.renderAllDebriefs();
    this.renderProgress();
    this.renderAllTreeBodies();
    this.renderTreeProgress();
    this.showActionFeedback('All simulator data deleted from this browser.');
  }

  showActionFeedback(message) {
    this.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.actionFeedback.textContent === message) this.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('simulator-scenarios')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new LeadershipScenarioSimulator();
});
